import { z } from 'zod'

import { env } from 'src/config/env'
import { convertKeysToCamelCase } from 'src/shared/utils/case-conversion'

import { logger } from './logger'

const TOKEN_KEY = 'authToken'
const API_BASE_URL = `${env.BACKEND_URL}`

const AuthTokensSchema = z.object({
  idToken: z.string(),
  accessToken: z.string(),
  refreshToken: z.string(),
})

export type AuthTokensType = z.infer<typeof AuthTokensSchema>

export function storeTokens(tokens: AuthTokensType) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
}

export function getTokens(): AuthTokensType | null {
  const stored = localStorage.getItem(TOKEN_KEY)
  if (!stored) return null

  try {
    const parsed = AuthTokensSchema.parse(JSON.parse(stored))
    return parsed
  } catch (error) {
    logger.error('Error parsing consumer auth tokens:', error)
    clearTokens()
    return null
  }
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY)
}

// Refresh tokens endpoint
export async function refreshTokens(): Promise<boolean> {
  // First check if we have tokens in local storage. If not, return false.
  const tokens = getTokens()
  if (!tokens) return false
  const refreshToken = tokens.refreshToken
  if (!refreshToken) return false

  try {
    // Then make the request to the refresh tokens endpoint, with the refresh token.
    const response = await fetch(`${API_BASE_URL}/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) return false

    // Parse the response and store the new tokens in local storage.
    const data = await response.json()
    const parsedData = convertKeysToCamelCase(data)

    storeTokens({
      idToken: parsedData.tokens.idToken,
      accessToken: parsedData.tokens.accessToken,
      refreshToken: parsedData.tokens.refreshToken,
    })
    return true
  } catch {
    return false
  }
}
